import {
  Activity,
  CalendarCheck,
  HeartPulse,
  type LucideIcon,
  Network,
  Scale,
  TrendingUp,
} from 'lucide-react';

import type { DeliveryReason, Insight, InsightCategory, InsightImpact } from '../../services/insightsService';

// The backend sends Spanish labels and summaries (`type_label_es`, `summary_es`),
// so new detector types render without a frontend release. What lives here is
// only what the API leaves to the client: icons, the delivery chip, and the
// figures behind each summary.

export const CATEGORY_ORDER: Exclude<InsightCategory, 'daily'>[] = [
  'physiology',
  'adherence',
  'training',
  'body',
  'patterns',
];

export const CATEGORY_ICON: Record<InsightCategory, LucideIcon> = {
  daily: CalendarCheck,
  adherence: Activity,
  physiology: HeartPulse,
  training: TrendingUp,
  body: Scale,
  patterns: Network,
};

// ---------------------------------------------------------------- numbers

const nf = (digits: number) =>
  new Intl.NumberFormat('es-ES', { maximumFractionDigits: digits, minimumFractionDigits: 0 });

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Spanish number, `null` for anything that is not a finite number. */
export const fmt = (v: unknown, digits = 1): string | null => (isNum(v) ? nf(digits).format(v) : null);

const signed = (v: number, digits = 1) => `${v > 0 ? '+' : ''}${nf(digits).format(v)}`;

const UNIT_LABEL: Record<string, string> = {
  pct: '%',
  min: 'min',
  ms: 'ms',
  bpm: 'lpm',
  steps: 'pasos',
  kcal: 'kcal',
  kg: 'kg',
  cm: 'cm',
};

export const withUnit = (v: unknown, unit?: string | null, digits = 1): string | null => {
  const n = fmt(v, digits);
  if (n === null) return null;
  const u = unit ? (UNIT_LABEL[unit] ?? unit) : '';
  return u === '%' ? `${n}%` : u ? `${n} ${u}` : n;
};

const pct = (ratio: unknown, digits = 0) => (isNum(ratio) ? `${nf(digits).format(ratio * 100)}%` : null);

const ci = (range: unknown, unit?: string | null) =>
  Array.isArray(range) && isNum(range[0]) && isNum(range[1])
    ? `${fmt(range[0])} a ${withUnit(range[1], unit)}`
    : null;

const minutesToHours = (minutes: unknown) =>
  isNum(minutes) ? `${Math.floor(minutes / 60)} h ${Math.round(minutes % 60)} min` : null;

// ---------------------------------------------------------------- dates

/** "2026-09-25" is a calendar day, not an instant: parse it as local midnight so
 *  it never slips to the previous day west of UTC. */
const parseDay = (iso: string) => new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);

export const formatDay = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = parseDay(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

export const formatWindow = (w: { start: string; end: string } | null | undefined): string | null =>
  w?.start && w?.end ? `${formatDay(w.start)} – ${formatDay(w.end)}` : null;

// Relative time in Spanish (e.g. "hace 2 h", "ayer").
export const timeAgo = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const min = Math.round((Date.now() - then) / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'ayer';
  if (days < 7) return `hace ${days} días`;
  return formatDay(iso);
};

// ---------------------------------------------------------------- engine pause

/** The engine skips patients who have not opened the app in this many days. */
export const INACTIVE_AFTER_DAYS = 15;

/**
 * Why the engine is not producing findings for this patient, or null when it
 * is. Mirrors the backend rule: no new findings and no messages for a patient
 * who never opened the app or has not in the last 15 days.
 */
export const enginePausedReason = (lastActivityAt: string | null | undefined, now = Date.now()): string | null => {
  if (lastActivityAt === undefined) return null; // not known yet
  if (lastActivityAt === null) return 'El paciente nunca ha abierto la app, así que no se generan insights.';
  const last = new Date(lastActivityAt).getTime();
  if (Number.isNaN(last)) return null;
  const days = Math.floor((now - last) / 86_400_000);
  return days > INACTIVE_AFTER_DAYS
    ? `El paciente no usa la app desde hace ${days} días: no se generan insights nuevos hasta que vuelva.`
    : null;
};

// ---------------------------------------------------------------- delivery

export type DeliveryTone = 'sent' | 'app' | 'waiting' | 'muted';

/**
 * What the coach did with the finding. It reports, it never asks: every
 * finding of an enabled type is already in the patient's app, and the coach
 * alone decides whether it deserves a message.
 */
export const deliveryChip = (insight: Insight): { label: string; detail: string; tone: DeliveryTone } => {
  switch (insight.delivery_state) {
    case 'sent':
      return {
        label: insight.delivered_at ? `Enviado ${timeAgo(insight.delivered_at)}` : 'Enviado',
        detail: 'El coach se lo comentó al paciente en un mensaje. También está en su app.',
        tone: 'sent',
      };
    case 'held':
      return {
        label: 'En la app, sin mensaje',
        detail: 'El paciente lo ve en su app. El coach decidió que no merecía un mensaje.',
        tone: 'app',
      };
    case 'scheduled':
      return {
        label: 'Pendiente del coach',
        detail: 'Ya está en la app del paciente. El coach decidirá en su próxima revisión si le escribe.',
        tone: 'waiting',
      };
    case 'type_disabled':
      return {
        label: 'No visible para el paciente',
        detail: 'Este tipo de insight está desactivado: ni el paciente ni el coach lo ven.',
        tone: 'muted',
      };
    default:
      return { label: 'Sin mensaje', detail: NOT_SENT_REASON[insight.delivery_reason] ?? 'El coach no llegó a enviarlo.', tone: 'muted' };
  }
};

const NOT_SENT_REASON: Partial<Record<DeliveryReason, string>> = {
  stale: 'Caducó antes de que el coach lo considerara.',
  superseded: 'Lo sustituyó un hallazgo más reciente sobre lo mismo.',
  patient_opted_out: 'El paciente tiene los insights desactivados.',
  type_muted: 'El paciente ha silenciado este tipo de insight.',
};

// ---------------------------------------------------------------- impact

const IMPACT_METRIC: Record<string, { label: string; ratio: boolean }> = {
  item_taken_rate: { label: 'Cumplimiento del ítem', ratio: true },
  engaged_day_share: { label: 'Días con actividad en la app', ratio: true },
  exposure_share_arm_a: { label: 'Días con el hábito', ratio: true },
  complete_nutrition_days: { label: 'Días de nutrición completos', ratio: false },
  metric_mean: { label: 'Media de la métrica', ratio: false },
};

/** "Cumplimiento del ítem 0% → 100% en la semana siguiente", or null before it is computed. */
export const impactLine = (impact: InsightImpact | null): { text: string; improved: boolean | null } | null => {
  if (!impact || !isNum(impact.before) || !isNum(impact.after)) return null;
  const meta = IMPACT_METRIC[impact.metric] ?? { label: impact.metric, ratio: false };
  const show = (v: number) => (meta.ratio ? pct(v) : fmt(v));
  return {
    text: `${meta.label}: ${show(impact.before)} la semana antes → ${show(impact.after)} la semana después`,
    improved: impact.improved,
  };
};

// ---------------------------------------------------------------- evidence

export interface Figure {
  label: string;
  value: string;
}

const HRV = (metric?: string | null) => (metric ? `VFC (${metric.toUpperCase()})` : 'VFC');

const push = (out: Figure[], label: string, value: string | null | undefined) => {
  if (value) out.push({ label, value });
};

/**
 * The figures behind a finding's sentence, so a clinician can check them
 * without reading the payload. Unknown types return [] — `summary_es` already
 * carries the meaning.
 */
export const getFigures = (insight: Insight): Figure[] => {
  const p = insight.payload || {};
  const out: Figure[] = [];

  switch (insight.type) {
    case 'daily_recap': {
      if (isNum(p.items_total)) push(out, 'Ítems', `${p.items_marked ?? 0}/${p.items_total}`);
      const m = p.macros;
      if (m && isNum(m.protein_g))
        push(out, 'Proteína', `${fmt(m.protein_g, 0)}${isNum(m.protein_target_g) ? ` / ${fmt(m.protein_target_g, 0)}` : ''} g`);
      if (m && isNum(m.kcal))
        push(out, 'Energía', `${fmt(m.kcal, 0)}${isNum(m.kcal_target) ? ` / ${fmt(m.kcal_target, 0)}` : ''} kcal`);
      if (p.sleep) push(out, 'Sueño', [minutesToHours(p.sleep.total_min), withUnit(p.sleep.efficiency_pct, 'pct', 0)].filter(Boolean).join(' · '));
      return out;
    }
    case 'weekly_review': {
      if (isNum(p.items_total)) push(out, 'Ítems', `${p.items_done ?? 0}/${p.items_total}`);
      push(out, 'Mejor', p.top_win);
      push(out, 'A mejorar', p.top_gap);
      if (isNum(p.weight_trend_kg_per_week)) push(out, 'Peso', `${signed(p.weight_trend_kg_per_week, 2)} kg/sem`);
      if (isNum(p.waist_change_cm)) push(out, 'Cintura', `${signed(p.waist_change_cm)} cm`);
      return out;
    }
    case 'supplement_lapse':
    case 'medication_lapse':
    case 'activity_lapse':
      push(out, 'Días seguidos', fmt(p.streak_days, 0));
      if (isNum(p.scheduled_per_week)) push(out, 'Pauta', `~${fmt(p.scheduled_per_week, 0)}×/sem`);
      push(out, 'Para', p.purpose);
      return out;
    case 'recent_change':
      push(out, 'Reciente', fmt(p.recent, 2));
      push(out, 'Referencia', fmt(p.baseline, 2));
      if (isNum(p.z)) push(out, 'Frente a su variabilidad', `z ${fmt(p.z)}`);
      return out;
    case 'cross_signal':
    case 'adherence_outcome': {
      const unit = p.outcome_unit;
      const a = insight.type === 'cross_signal' ? p.group_a : 'Semanas con adherencia';
      const b = insight.type === 'cross_signal' ? p.group_b : 'Resto de semanas';
      const na = insight.type === 'cross_signal' ? p.n_a : p.weeks_a;
      const nb = insight.type === 'cross_signal' ? p.n_b : p.weeks_b;
      push(out, `${a} (n=${na ?? '?'})`, withUnit(p.mean_a, unit));
      push(out, `${b} (n=${nb ?? '?'})`, withUnit(p.mean_b, unit));
      push(out, 'IC 90 % diferencia', ci(p.ci90, unit));
      push(out, 'q', fmt(p.q_value, 3));
      return out;
    }
    case 'load_recovery_balance':
      push(out, 'Carga 7 días', fmt(p.acute_load, 0));
      push(out, 'Media semanal previa', fmt(p.chronic_weekly_load, 0));
      if (isNum(p.ratio)) push(out, 'Relación', `×${fmt(p.ratio, 2)}`);
      push(out, 'Método', p.load_method === 'srpe' ? 'RPE de sesión' : p.load_method === 'hr_zone' ? 'Zonas de FC' : p.load_method);
      push(out, `${HRV(p.hrv_metric)} z medio`, fmt(p.hrv_mean_z));
      push(out, 'FC reposo z medio', fmt(p.rhr_mean_z));
      return out;
    case 'rhr_trend':
      if (isNum(p.start_bpm) && isNum(p.end_bpm)) push(out, 'FC en reposo', `${fmt(p.start_bpm, 0)} → ${fmt(p.end_bpm, 0)} lpm`);
      push(out, 'Semanas', fmt(p.weeks, 0));
      if (isNum(p.training_min_per_week_first4) && isNum(p.training_min_per_week_last4))
        push(out, 'Entreno', `${fmt(p.training_min_per_week_first4, 0)} → ${fmt(p.training_min_per_week_last4, 0)} min/sem`);
      push(out, 'p', fmt(p.p_value, 3));
      return out;
    case 'recovery_profile':
      push(out, 'Mediana', isNum(p.median_recovery_nights) ? `${fmt(p.median_recovery_nights, 0)} noches` : null);
      push(out, 'Sesiones duras', fmt(p.n_sessions, 0));
      if (p.distribution)
        push(out, 'Reparto', Object.entries(p.distribution).map(([k, v]) => `${k}: ${v}`).join(' · '));
      return out;
    case 'cardio_efficiency':
      push(out, 'Actividad', p.activity);
      if (isNum(p.relative_change_pct))
        push(
          out,
          p.pace_adjusted && isNum(p.reference_speed_kmh)
            ? `Cambio a ritmo comparable (${fmt(p.reference_speed_kmh)} km/h)`
            : 'Cambio',
          `${signed(p.relative_change_pct)}%`,
        );
      // Walking findings since the pace adjustment; older ones lack these fields.
      if (p.pace_adjusted) {
        if (isNum(p.speed_change_pct)) push(out, 'Velocidad', `${signed(p.speed_change_pct)}%`);
        if (isNum(p.unadjusted_change_pct)) push(out, 'Cambio sin ajustar', `${signed(p.unadjusted_change_pct)}%`);
      }
      push(out, 'Sesiones', fmt(p.n_sessions, 0));
      push(out, 'p', fmt(p.p_value, 3));
      return out;
    case 'weight_trend':
      if (isNum(p.kg_per_week)) push(out, 'Ritmo', `${signed(p.kg_per_week, 2)} kg/sem (${signed(p.pct_per_week, 2)}%)`);
      if (isNum(p.change_28d_kg)) push(out, 'Cambio 28 días', `${signed(p.change_28d_kg)} kg`);
      if (isNum(p.previous_kg_per_week)) push(out, '28 días previos', `${signed(p.previous_kg_per_week, 2)} kg/sem`);
      push(out, 'Pesajes', fmt(p.n_weighins, 0));
      return out;
    case 'energy_balance':
      push(out, 'Gasto estimado', withUnit(p.est_expenditure_kcal, 'kcal', 0));
      push(out, 'Ingesta registrada', withUnit(p.mean_intake_kcal, 'kcal', 0));
      push(out, 'Objetivo del plan', withUnit(p.mean_target_kcal, 'kcal', 0));
      push(out, 'Días completos', fmt(p.complete_days, 0));
      return out;
    case 'logging_gap':
      push(out, 'Ingesta registrada', withUnit(p.mean_intake_kcal, 'kcal', 0));
      if (isNum(p.kg_per_week)) push(out, 'Peso', `${signed(p.kg_per_week, 2)} kg/sem`);
      push(out, 'Días completos', fmt(p.complete_days, 0));
      push(out, 'Días con alguna comida', fmt(p.days_with_any_meal_logged, 0));
      return out;
    default:
      return out;
  }
};

/** physio_deviation is read signal by signal: today's value against the patient's usual. */
export const physioSignals = (insight: Insight) => {
  if (insight.type !== 'physio_deviation') return [];
  const s = insight.payload?.signals || {};
  const rows: { key: string; label: string; value: string | null; usual: string | null; flagged: boolean }[] = [];
  const add = (key: string, label: string, unit: string, digits = 0) => {
    const sig = s[key];
    if (!sig) return;
    rows.push({
      key,
      label,
      value: key === 'sleep_total_min' ? minutesToHours(sig.value) : withUnit(sig.value, unit, digits),
      usual: key === 'sleep_total_min' ? minutesToHours(sig.baseline_median) : withUnit(sig.baseline_median, unit, digits),
      flagged: Boolean(sig.flagged),
    });
  };
  add('hrv_night', HRV(insight.payload?.hrv_metric), 'ms');
  add('rhr', 'FC en reposo', 'bpm');
  add('sleep_total_min', 'Sueño total', 'min');
  return rows;
};
